/**
 * Everything the editor has running in the agent container, in one list.
 *
 * A build or a delivery is started from a dialog and then outlives it: the job
 * belongs to the editor (useSnapshotJobs), keeps going with the dialog closed,
 * and leaves its result to be picked up the next time that folder is opened.
 * That is what makes the dialogs usable — and it is also what made the work
 * invisible, because the only places a job showed were the snackbar, which is
 * gone in seconds, and the row it marks in the tree, which has to be scrolled
 * to and knows nothing about why it is marked.
 *
 * So this is the one place that answers "what is running, and how did the last
 * one end". It only READS the jobs: a finished job's result still belongs to
 * the dialog that asked for it, and `consume` stays there — clearing it from
 * here would throw away a build nobody has looked at yet. A task row opens its
 * own dialog instead, which is where the result is waiting.
 */

import React from "react";
import _ from "lodash";
import {
  Badge,
  CircularProgress,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import LaunchIcon from "@material-ui/icons/Launch";
import PlaylistPlayIcon from "@material-ui/icons/PlaylistPlay";
import { BAR_BG, DARK_SCROLLBAR_CSS, FIELD_LABEL, PANEL_BG } from "./panelPalette";

const BORDER = "1px solid rgba(255,255,255,0.09)";

const useStyles = makeStyles({
  paper: { width: "380px", backgroundColor: PANEL_BG, color: "#e6e6e6" },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    padding: "12px 14px",
    backgroundColor: BAR_BG,
    borderBottom: BORDER,
  },
  title: { fontSize: "13px", fontWeight: 500 },
  list: { flex: 1, overflowY: "auto", padding: "10px 12px" },
  empty: { fontSize: "12px", color: FIELD_LABEL, lineHeight: 1.6, padding: "6px 2px" },
  job: {
    border: BORDER,
    borderRadius: "6px",
    padding: "10px 12px",
    marginBottom: "8px",
    backgroundColor: BAR_BG,
  },
  jobHead: { display: "flex", alignItems: "baseline", gap: "8px" },
  noun: { fontSize: "12px", fontWeight: 600, flex: "0 0 auto" },
  target: {
    flex: 1,
    minWidth: 0,
    fontSize: "12px",
    color: FIELD_LABEL,
    wordBreak: "break-word",
  },
  when: { flex: "0 0 auto", fontSize: "11px", color: FIELD_LABEL, opacity: 0.8 },
  line: { fontSize: "11px", marginTop: "6px", lineHeight: 1.5, color: FIELD_LABEL },
  running: { display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" },
  ok: { color: "#a5d6a7" },
  warn: { color: "#e0b96a" },
  bad: { color: "#ef9a9a" },
  open: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    marginTop: "8px",
    padding: 0,
    border: "none",
    background: "none",
    color: FIELD_LABEL,
    fontSize: "11px",
    cursor: "pointer",
    "&:hover": { color: "#4eabb1" },
  },
  note: {
    fontSize: "11px",
    color: FIELD_LABEL,
    opacity: 0.8,
    lineHeight: 1.5,
    padding: "10px 14px",
    borderTop: BORDER,
  },
});

/** "4m ago", "just now" — a job is read in minutes, never in seconds. */
function since(at) {
  if (!at) return "";
  const seconds = Math.max(0, Math.round((Date.now() - at) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.round(minutes / 60)}h ago`;
}

/** The folder's own name — a task is known by it, not by its path. */
function targetName(target) {
  return String(target || "").split("/").pop() || "the editor";
}

/**
 * How a finished job ended, in one line.
 *
 * Read off the job rather than off its response body: `outcome` is the snackbar
 * variant the job settled with, so this says the same thing the person was told
 * when it happened — and says it after the snackbar is long gone.
 */
function describeOutcome(job) {
  if (_.get(job, "response.ok") === false) return ["Failed — open it to see why.", "bad"];
  if (job.outcome === "warning") return ["Finished, with something to look at.", "warn"];
  if (job.outcome === "info") return ["Finished — nothing was sent.", null];
  return ["Finished.", "ok"];
}

/**
 * @param {object[]} jobs       `editor.jobs.list`
 * @param {function} progressOf `editor.jobs.progressOf`
 * @param {function} onOpenTask called with a folder path, to open its dialog
 */
export default function DrawerJobs({ open, jobs, progressOf, onOpenTask, onClose }) {
  const classes = useStyles();

  return (
    <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.paper }}>
      <style>{DARK_SCROLLBAR_CSS}</style>
      <div className={classes.head}>
        <Typography className={classes.title}>Background tasks</Typography>
        <IconButton size="small" onClick={onClose} style={{ color: FIELD_LABEL }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div className={`${classes.list} proto-dark-scroll`}>
        {jobs.length === 0 ? (
          <Typography className={classes.empty}>
            Nothing is running. A build, a submit or an export keeps going in the agent container
            after its dialog is closed, and shows up here while it does.
          </Typography>
        ) : (
          jobs.map((job) => {
            const isRunning = job.state === "running";
            const [outcome, tone] = isRunning ? [null, null] : describeOutcome(job);
            const progress = isRunning ? progressOf(job) : null;

            return (
              <div className={classes.job} key={job.key}>
                <div className={classes.jobHead}>
                  <span className={classes.noun}>{job.noun}</span>
                  <span className={classes.target}>{targetName(job.target)}</span>
                  <span className={classes.when}>
                    {since(isRunning ? job.startedAt : job.finishedAt)}
                  </span>
                </div>

                {isRunning ? (
                  <div className={classes.running}>
                    <CircularProgress size={14} />
                    <span className={classes.line} style={{ marginTop: 0 }}>
                      {progress || "Running in the agent container."}
                    </span>
                  </div>
                ) : (
                  <div className={`${classes.line} ${tone ? classes[tone] : ""}`}>{outcome}</div>
                )}

                {/* The connection, not the work: the container runs on without
                    us, so this says the page lost sight of it, not that it stopped. */}
                {job.connectionDown && (
                  <div className={`${classes.line} ${classes.warn}`}>
                    Lost contact with the server — the container is still working.
                  </div>
                )}

                {job.folder && (
                  <button
                    type="button"
                    className={classes.open}
                    onClick={() => {
                      onOpenTask(job.folder);
                      onClose();
                    }}
                  >
                    <LaunchIcon style={{ fontSize: "13px" }} />
                    Open this task
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      <Typography className={classes.note}>
        A finished job stays here until its own dialog has shown you the result.
      </Typography>
    </Drawer>
  );
}

/** The button that opens the drawer, with what is running on it. */
export function JobsButton({ runningCount, onClick }) {
  return (
    <Tooltip
      title={
        runningCount
          ? `${runningCount} background task${runningCount === 1 ? "" : "s"} running`
          : "Background tasks"
      }
    >
      <IconButton size="small" onClick={onClick}>
        <Badge badgeContent={runningCount} color="primary" overlap="rectangular">
          <PlaylistPlayIcon fontSize="small" />
        </Badge>
      </IconButton>
    </Tooltip>
  );
}
